/**
 * The only place this panel talks to the API.
 *
 * Everything goes through `request`, which means four things are true everywhere rather
 * than in most places: the token is attached, a 401 ends the session once instead of
 * leaving each screen to notice separately, the server's error message reaches the user
 * rather than being replaced by "Something went wrong", and a failed call throws a typed
 * error a screen can branch on.
 *
 * SMA's admin repeated its fetch logic per screen and handled failures inconsistently, so
 * an expired session showed as an empty table on one page and a blank screen on another.
 */

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api';
const TOKEN_KEY = 'xniq.accessToken';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The caller is signed in but not allowed. Worth distinguishing in the UI. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** A rule refused the operation — a claimed case, a published version, a name in use. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

export function storedToken(): string | null {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string | null): void {
  if (token === null) window.localStorage.removeItem(TOKEN_KEY);
  else window.localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Called when the server rejects the token.
 *
 * A callback rather than a redirect here, so this module stays free of routing and can be
 * reasoned about on its own.
 */
let onUnauthenticated: () => void = () => {};

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Suppresses the session-ended handler, for the sign-in call itself. */
  anonymous?: boolean;
}

/**
 * Fetch a file and hand back an object URL for it.
 *
 * Images cannot go straight into `<img src>` here: the token lives in `localStorage` and is
 * attached by this module, and a browser loading an image sends none of that. So the bytes
 * come down the authenticated path like everything else and the caller points the `<img>`
 * at the result — and must revoke it when finished.
 *
 * A 401 ends the session exactly as it does for `request`, or a panel whose token expired
 * would keep showing broken photographs while every other screen redirected.
 */
export async function fetchBlobUrl(path: string): Promise<string> {
  const token = storedToken();
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: token === null ? {} : { authorization: `Bearer ${token}` },
  });

  if (response.status === 401) {
    storeToken(null);
    onUnauthenticated();
    throw new ApiError(401, 'Your session has ended. Sign in again.');
  }

  if (!response.ok) {
    throw new ApiError(response.status, `The file could not be loaded (${response.status}).`);
  }

  return URL.createObjectURL(await response.blob());
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = storedToken();
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (token !== null && options.anonymous !== true) headers['authorization'] = `Bearer ${token}`;

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    // Spread rather than pass undefined: under exactOptionalPropertyTypes an absent body
    // and an explicitly-undefined one are different types, and fetch wants the former.
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  if (response.status === 401 && options.anonymous !== true) {
    // Handled once, centrally. Otherwise every screen grows its own copy of this and one
    // of them forgets, leaving a signed-out user staring at a page that never loads.
    storeToken(null);
    onUnauthenticated();
    throw new ApiError(401, 'Your session has ended. Sign in again.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text === '' ? null : safeParse(text);

  if (!response.ok) {
    // The server's own message, not a generic one. It was written to be shown — "This
    // visit is complete and cannot be changed" tells a user what to do next in a way
    // "Request failed" never will.
    const message =
      isRecord(payload) && typeof payload['error'] === 'string'
        ? payload['error']
        : `Request failed (${response.status})`;

    throw new ApiError(response.status, message, isRecord(payload) ? payload['details'] : undefined);
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // A non-JSON body from an API that always sends JSON means something upstream
    // answered instead — a proxy error page, usually. Keep it rather than discarding it.
    return { error: text.slice(0, 200) };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
